export interface TimeseriesPoint {
    timestamp: number;
    value: number;
}

export interface MetricTimeseries {
    name: string;
    data: TimeseriesPoint[];
}

export interface MetricCategory {
    category: string;
    metrics: MetricTimeseries[] | null;
}

const GROUPED_CATEGORIES = ["ram", "memory", "network", "cpu"];

export function shouldGroupMetrics(category: string): boolean {
    const lowerCategory = category.toLowerCase();
    return GROUPED_CATEGORIES.some((keyword) => lowerCategory.includes(keyword));
}

export function combineMetricsForChart(
    metrics: MetricTimeseries[],
): { data: { timestamp: number; [key: string]: number }[]; keys: string[] } {
    const timestampMap = new Map<number, { timestamp: number; [key: string]: number }>();
    const keys: string[] = [];

    for (const metric of metrics) {
        keys.push(metric.name);
        for (const point of metric.data) {
            const normalizedTs = normalizeTimestamp(point.timestamp);
            if (!timestampMap.has(normalizedTs)) {
                timestampMap.set(normalizedTs, { timestamp: normalizedTs });
            }
            const entry = timestampMap.get(normalizedTs)!;
            entry[metric.name] = point.value;
        }
    }

    const data = Array.from(timestampMap.values()).sort((a, b) => a.timestamp - b.timestamp);
    return { data, keys };
}

function normalizeTimestamp(ts: number): number {
    // If timestamp is in seconds (less than year 3000 in ms), convert to milliseconds
    // Unix timestamps in seconds are ~10 digits, in milliseconds ~13 digits
    if (ts < 10000000000) {
        return ts * 1000;
    }
    return ts;
}

// We generally do one big load for the last hour of data, and then subsequent polls are restricted to only pulling the last 30s of data. 
// This function allows us to append to the existing datasets.
export function mergeMetricsData(
    existing: MetricCategory[],
    incoming: MetricCategory[],
): MetricCategory[] {
    const merged: MetricCategory[] = [];

    for (const incomingCategory of incoming) {
        const existingCategory = existing.find((c) => c.category === incomingCategory.category);

        if (!existingCategory) {
            merged.push(incomingCategory);
            continue;
        }

        const mergedMetrics: MetricTimeseries[] = [];

        for (const incomingMetric of incomingCategory.metrics || []) {
            const existingMetric = existingCategory.metrics?.find((m) => m.name === incomingMetric.name);

            if (!existingMetric) {
                mergedMetrics.push(incomingMetric);
                continue;
            }

            const existingTimestamps = new Set(existingMetric.data.map((d) => d.timestamp));
            const newPoints = incomingMetric.data.filter((d) => !existingTimestamps.has(d.timestamp));
            const mergedData = [...existingMetric.data, ...newPoints].sort(
                (a, b) => a.timestamp - b.timestamp,
            );

            mergedMetrics.push({
                name: incomingMetric.name,
                data: mergedData,
            });
        }

        merged.push({
            category: incomingCategory.category,
            metrics: mergedMetrics,
        });
    }

    return merged;
}

export function getLatestValue(data: TimeseriesPoint[]): number | null {
    if (!data || data.length === 0) return null;
    return data[data.length - 1].value;
}

export function transformToChartData(
    data: TimeseriesPoint[],
): { timestamp: number; value: number }[] {
    return data.map((point) => ({
        timestamp: normalizeTimestamp(point.timestamp),
        value: point.value,
    }));
}

